import { IS_SLIDE_BUILDER_ENABLED } from '@/components/constants/flags';
import { TabType } from '@/hooks/query/useQuerySessionWindows';
import type { UiOpenArtifactWire } from '@heidi/agents/contracts';
import type { Artifact } from '@heidi/agents/data';

// The wire payload is declared in `@heidi/agents/contracts` — history:
// `git log --follow src/features/agent-events/ui-tools/map-open-artifact.ts`.
// Re-exported name-intact so every existing reader keeps its specifier.
export type { UiOpenArtifactWire };

/** Map a validated open_artifact wire payload to the Projects canvas Artifact. */
export const mapUiOpenArtifactData = (data: UiOpenArtifactWire): Artifact | null => {
  const focusTab =
    data.active_tab && Object.values(TabType).includes(data.active_tab as TabType)
      ? (data.active_tab as TabType)
      : undefined;

  switch (data.type) {
    case 'session':
      return data.session_id
        ? {
            type: 'session',
            sessionId: data.session_id,
            ...(focusTab ? { focusTab } : {}),
          }
        : null;
    case 'note':
      return data.session_id ? { type: 'note', sessionId: data.session_id } : null;
    case 'tasks':
      return data.session_id ? { type: 'tasks', sessionId: data.session_id } : null;
    case 'codes':
      return data.session_id ? { type: 'codes', sessionId: data.session_id } : null;
    case 'document':
      return data.session_id && data.document_id
        ? {
            type: 'document',
            sessionId: data.session_id,
            documentId: data.document_id,
          }
        : null;
    case 'letter':
      return data.session_id && data.letter_id
        ? { type: 'letter', sessionId: data.session_id, letterId: data.letter_id }
        : null;
    case 'patient':
      return data.patient_id ? { type: 'patient', patientId: data.patient_id } : null;
    case 'template-builder':
      return data.template_id
        ? { type: 'template-builder', templateId: data.template_id }
        : null;
    case 'form-template':
      return data.template_id
        ? { type: 'form-template', templateId: data.template_id }
        : null;
    case 'presentation':
      if (!IS_SLIDE_BUILDER_ENABLED()) return null;
      return data.presentation_id
        ? { type: 'presentation', presentationId: data.presentation_id }
        : null;
    case 'memory':
      return data.memory_id ? { type: 'memory', memoryId: data.memory_id } : null;
    case 'workflow':
      return data.workflow_id ? { type: 'workflow', workflowId: data.workflow_id } : null;
    case 'session-list':
      return data.session_ids?.length
        ? {
            type: 'session-list',
            sessionIds: data.session_ids,
            ...(data.start_date ? { startDate: data.start_date } : {}),
            ...(data.end_date ? { endDate: data.end_date } : {}),
          }
        : null;
    default:
      return null;
  }
};

// XXX: previews the Work canvas never opens (AGE-2417: a preview is one object). The mapping above
// stays whole because classic Scribe still refreshes and opens the session's tasks / codes panels.
const NOT_A_WORK_PREVIEW: ReadonlySet<Artifact['type']> = new Set([
  'tasks',
  'codes',
  'template-builder',
  'form-template',
  'session-list',
]);

export const isWorkPreviewArtifact = (artifact: Artifact): boolean =>
  !NOT_A_WORK_PREVIEW.has(artifact.type);
